import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Input,
  Label,
  Form,
  FormGroup
} from 'reactstrap';
import classnames from 'classnames';
import TableContainer from '../../../../Components/Common/TableContainerReactTable';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';

export interface AccountOwnerData {
  id: string;
  name: string;
  location: string;
  type: string;
  industry: string;
  active: boolean;
  mainPhone: string;
  address: string;
  supplier: boolean;
  parentName: string;
}

interface AccountOwnerFormData {
  accountId: string;
  name: string;
  location: string;
  tradingName: string;
  type: string;
  industry: string;
  acn: string;
  abn: string;
  statusValue: string;
  rating: string;
  mainPhoneNumber: string;
  mainFaxNumber: string;
  mainEmailAddress: string;
  webAddress: string;
  accountOwner: string;
  primaryContact: string;
  parentAccount: string;
  paymentTerms: string;
  active: boolean;
  supplier: boolean;
  // Billing Address
  billingAddr1: string;
  billingAddr2: string;
  billingAddr3: string;
  billingPostCode: string;
  billingCountry: string;
  billingStateProvince: string;
  // Shipping Address
  shippingAddr1: string;
  shippingAddr2: string;
  shippingAddr3: string;
  shippingPostCode: string;
  shippingCountry: string;
  shippingStateProvince: string;
  copyFromBilling: boolean;
  // Order Defaults
  warehouse: string;
  priceList: string;
  deliveryComments: string;
}

interface AccountOwnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (accountOwner: AccountOwnerData) => void;
}

const mockAccountOwners: AccountOwnerData[] = [
  {
    id: '1',
    name: 'ABC Corporation',
    location: 'Auckland',
    type: 'Customer',
    industry: 'Manufacturing',
    active: true,
    mainPhone: '+64 9 123 4567',
    address: '123 Queen Street, Auckland',
    supplier: false,
    parentName: 'ABC Holdings Ltd'
  },
  {
    id: '2',
    name: 'XYZ Suppliers Ltd',
    location: 'Wellington',
    type: 'Supplier',
    industry: 'Wholesale',
    active: false,
    mainPhone: '+64 4 987 6543',
    address: '456 Lambton Quay, Wellington',
    supplier: true,
    parentName: ''
  },
  {
    id: '3',
    name: 'Tech Solutions Inc',
    location: 'Christchurch',
    type: 'Customer',
    industry: 'Technology',
    active: true,
    mainPhone: '+64 3 555 0123',
    address: '789 Colombo Street, Christchurch',
    supplier: false,
    parentName: 'Tech Group NZ'
  },
  {
    id: '4',
    name: 'Green Energy Co',
    location: 'Hamilton',
    type: 'Both',
    industry: 'Energy',
    active: true,
    mainPhone: '+64 7 444 5678',
    address: '321 Victoria Street, Hamilton',
    supplier: true,
    parentName: ''
  },
  {
    id: '5',
    name: 'Food Distributors Ltd',
    location: 'Tauranga',
    type: 'Supplier',
    industry: 'Food & Beverage',
    active: true,
    mainPhone: '+64 7 333 9876',
    address: '654 Cameron Road, Tauranga',
    supplier: true,
    parentName: 'Food Holdings Group'
  }
];

const defaultAccountForm: AccountOwnerFormData = {
  accountId: '',
  name: '',
  location: '',
  tradingName: '',
  type: 'Customer',
  industry: '',
  acn: '',
  abn: '',
  statusValue: 'Active',
  rating: '',
  mainPhoneNumber: '',
  mainFaxNumber: '',
  mainEmailAddress: '',
  webAddress: '',
  accountOwner: '',
  primaryContact: '',
  parentAccount: '',
  paymentTerms: 'COD',
  active: true,
  supplier: false,
  billingAddr1: '',
  billingAddr2: '',
  billingAddr3: '',
  billingPostCode: '',
  billingCountry: 'Australia',
  billingStateProvince: '',
  shippingAddr1: '',
  shippingAddr2: '',
  shippingAddr3: '',
  shippingPostCode: '',
  shippingCountry: 'Australia',
  shippingStateProvince: '',
  copyFromBilling: false,
  warehouse: 'Main Warehouse',
  priceList: 'Default Price List',
  deliveryComments: ''
};

const searchFieldOptions = ['Name', 'Account Id', 'Location', 'Type', 'Industry'];
const typeOptions = ['Customer', 'Supplier', 'Both', 'Prospect'];
const industryOptions = ['Manufacturing', 'Retail', 'Technology', 'Healthcare', 'Finance', 'Education', 'Other'];
const statusOptions = ['Active', 'Inactive', 'Pending', 'Suspended'];
const ratingOptions = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D', 'F'];
const paymentTermsOptions = ['COD', 'Net 7', 'Net 15', 'Net 30', 'Net 60', 'Net 90', 'Prepaid'];
const countryOptions = ['Australia', 'New Zealand', 'United States', 'United Kingdom', 'Canada'];
const stateProvinceOptions = [
  'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland',
  'South Australia', 'Tasmania', 'Victoria', 'Western Australia',
  'Auckland', 'Wellington', 'Canterbury', 'Otago'
];
const warehouseOptions = ['Main Warehouse', 'Secondary Warehouse', 'Distribution Center'];
const priceListOptions = ['Default Price List', 'Premium Price List', 'Wholesale Price List'];

const AccountOwnerModal: React.FC<AccountOwnerModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [activeTab, setActiveTab] = useState('1');
  const [searchField, setSearchField] = useState('Name');
  const [searchValue, setSearchValue] = useState('');
  const [accountOwners, setAccountOwners] = useState<AccountOwnerData[]>(mockAccountOwners);
  const [selectedAccountOwner, setSelectedAccountOwner] = useState<AccountOwnerData | null>(null);
  const [accountForm, setAccountForm] = useState<AccountOwnerFormData>(defaultAccountForm);

  const columns = useMemo(() => [
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAccountOwner?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Location',
      accessorKey: 'location',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAccountOwner?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Type',
      accessorKey: 'type',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAccountOwner?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Industry',
      accessorKey: 'industry',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAccountOwner?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Active',
      accessorKey: 'active',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAccountOwner?.id === cell.row.original.id ? 'table-active' : ''}
        >
          <span className={`badge ${cell.getValue() ? 'bg-success' : 'bg-secondary'}`}>
            {cell.getValue() ? 'Yes' : 'No'}
          </span>
        </div>
      )
    },
    {
      header: 'Main Phone',
      accessorKey: 'mainPhone',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAccountOwner?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Address',
      accessorKey: 'address',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAccountOwner?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Supplier',
      accessorKey: 'supplier',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAccountOwner?.id === cell.row.original.id ? 'table-active' : ''}
        >
          <span className={`badge ${cell.getValue() ? 'bg-success' : 'bg-secondary'}`}>
            {cell.getValue() ? 'Yes' : 'No'}
          </span>
        </div>
      )
    },
    {
      header: 'Parent Name',
      accessorKey: 'parentName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAccountOwner?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    }
  ], [selectedAccountOwner]);

  const handleSearch = () => {
    const filtered = mockAccountOwners.filter(account => {
      let fieldValue = '';
      switch (searchField) {
        case 'Name':
          fieldValue = account.name;
          break;
        case 'Location':
          fieldValue = account.location;
          break;
        case 'Type':
          fieldValue = account.type;
          break;
        case 'Industry':
          fieldValue = account.industry;
          break;
        default:
          fieldValue = account.name;
      }
      return fieldValue.toLowerCase().includes(searchValue.toLowerCase());
    });
    setAccountOwners(filtered);
  };

  const handleClear = () => {
    setSearchValue('');
    setAccountOwners(mockAccountOwners);
    setSelectedAccountOwner(null);
  };

  const handleRowClick = (accountOwner: AccountOwnerData) => {
    setSelectedAccountOwner(accountOwner);
    // Populate view form with selected data
    setAccountForm({
      ...accountForm,
      accountId: accountOwner.id,
      name: accountOwner.name,
      location: accountOwner.location,
      type: accountOwner.type,
      industry: accountOwner.industry,
      active: accountOwner.active,
      mainPhoneNumber: accountOwner.mainPhone,
      supplier: accountOwner.supplier,
      parentAccount: accountOwner.parentName
    });
  };

  const handleRowDoubleClick = (accountOwner: AccountOwnerData) => {
    if (onSelect) {
      onSelect(accountOwner);
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedAccountOwner(null);
    onClose();
  };

  const handleSelect = () => {
    if (selectedAccountOwner && onSelect) {
      onSelect(selectedAccountOwner);
    }
    onClose();
  };

  const handleAccountFormChange = (field: keyof AccountOwnerFormData, value: any) => {
    setAccountForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAccountFormCheckbox = (field: keyof AccountOwnerFormData) => {
    setAccountForm(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleCopyFromBilling = () => {
    if (accountForm.copyFromBilling) {
      setAccountForm(prev => ({
        ...prev,
        shippingAddr1: prev.billingAddr1,
        shippingAddr2: prev.billingAddr2,
        shippingAddr3: prev.billingAddr3,
        shippingPostCode: prev.billingPostCode,
        shippingCountry: prev.billingCountry,
        shippingStateProvince: prev.billingStateProvince
      }));
    }
  };

  const handleNewAccountClear = () => setAccountForm(defaultAccountForm);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="xl">
      <ModalHeader toggle={onClose}>Account</ModalHeader>
      <ModalBody>
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              onClick={() => setActiveTab('1')}
              style={{ cursor: 'pointer' }}
            >
              Search
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '2' })}
              onClick={() => setActiveTab('2')}
              style={{ cursor: 'pointer' }}
            >
              Adv Search
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '3' })}
              onClick={() => setActiveTab('3')}
              style={{ cursor: 'pointer' }}
            >
              View
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '4' })}
              onClick={() => setActiveTab('4')}
              style={{ cursor: 'pointer' }}
            >
              New
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* Search Tab */}
          <TabPane tabId="1">
            <Form>
              <Row className="mb-2 justify-content-center align-items-center gap-2">
                <Col md={4}>
                  <SelectInput
                    formData={{ searchField }}
                    setFormData={(obj) => setSearchField(obj.searchField)}
                    label="Search"
                    id="searchField"
                    options={searchFieldOptions}
                  />
                </Col>
                <Col md={4}>
                  <FormInput
                    id="searchValue"
                    label="For value"
                    value={searchValue}
                    onChange={setSearchValue}
                    type="text"
                  />
                </Col>
                <Col md={1} className="me-3">
                  <button
                    onClick={handleSearch}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-primary btn-sm"
                  >
                    Search
                  </button>
                </Col>
                <Col md={1}>
                  <button
                    onClick={handleClear}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-warning btn-sm"
                  >
                    Clear
                  </button>
                </Col>
              </Row>

              {/* Account Owner Table */}
              <Row>
                <Col xs={12}>
                  <TableContainer
                    columns={columns}
                    data={accountOwners || []}
                    isGlobalFilter={false}
                    customPageSize={10}
                    divClass="mb-0"
                    tableClass="table-centered align-middle table-nowrap mb-0"
                    theadClass="table-light"
                    thClass="px-3 py-2"
                  />
                </Col>
              </Row>
            </Form>
          </TabPane>

          {/* Advanced Search Tab */}
          <TabPane tabId="2">
            <Row className="justify-content-center align-items-center">
              <Col md={12} className="text-center">
                <p className="text-muted">Advanced Search functionality coming soon...</p>
              </Col>
            </Row>
          </TabPane>

          {/* View Tab */}
          <TabPane tabId="3">
            <Form>
              <Row className="mb-2">
                <Col md={2}>
                  <FormInput
                    id="viewAccountId"
                    label="Account Id"
                    value={accountForm.accountId}
                    onChange={(val) => handleAccountFormChange('accountId', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewName"
                    label="Name *"
                    value={accountForm.name}
                    onChange={(val) => handleAccountFormChange('name', val)}
                    required
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewLocation"
                    label="Location"
                    value={accountForm.location}
                    onChange={(val) => handleAccountFormChange('location', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewTradingName"
                    label="Trading Name"
                    value={accountForm.tradingName}
                    onChange={(val) => handleAccountFormChange('tradingName', val)}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Type"
                    id="type"
                    options={typeOptions}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Industry *"
                    id="industry"
                    options={industryOptions}
                  />
                </Col>

              </Row>
              <Row className="mb-2">
                <Col md={2}>
                  <FormInput
                    id="viewACN"
                    label="ACN"
                    value={accountForm.acn}
                    onChange={(val) => handleAccountFormChange('acn', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="viewABN"
                    label="ABN"
                    value={accountForm.abn}
                    onChange={(val) => handleAccountFormChange('abn', val)}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Status"
                    id="statusValue"
                    options={statusOptions}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Rating"
                    id="rating"
                    options={ratingOptions}
                  />
                </Col>
                <Col md={2}>
                  <div className="d-flex gap-2 mt-4">
                    <div className="form-check">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="viewActive"
                        checked={accountForm.active}
                        onChange={() => handleAccountFormCheckbox('active')}
                      />
                      <Label className="form-check-label" htmlFor="viewActive">
                        Active
                      </Label>
                    </div>
                    <div className="form-check">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="viewSupplier"
                        checked={accountForm.supplier}
                        onChange={() => handleAccountFormCheckbox('supplier')}
                      />
                      <Label className="form-check-label" htmlFor="viewSupplier">
                        Supplier
                      </Label>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={3}>
                  <FormInput
                    id="viewMainPhoneNumber"
                    label="Main Phone Number"
                    value={accountForm.mainPhoneNumber}
                    onChange={(val) => handleAccountFormChange('mainPhoneNumber', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="viewMainFaxNumber"
                    label="Main Fax Number"
                    value={accountForm.mainFaxNumber}
                    onChange={(val) => handleAccountFormChange('mainFaxNumber', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="viewMainEmailAddress"
                    label="Main Email Address"
                    value={accountForm.mainEmailAddress}
                    onChange={(val) => handleAccountFormChange('mainEmailAddress', val)}
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="viewWebAddress"
                    label="Web Address"
                    value={accountForm.webAddress}
                    onChange={(val) => handleAccountFormChange('webAddress', val)}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={3}>
                  <FormInput
                    id="viewAccountOwner"
                    label="Account Owner"
                    value={accountForm.accountOwner}
                    onChange={(val) => handleAccountFormChange('accountOwner', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="viewPrimaryContact"
                    label="Primary Contact"
                    value={accountForm.primaryContact}
                    onChange={(val) => handleAccountFormChange('primaryContact', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="viewParentAccount"
                    label="Parent Account"
                    value={accountForm.parentAccount}
                    onChange={(val) => handleAccountFormChange('parentAccount', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Payment Terms"
                    id="paymentTerms"
                    options={paymentTermsOptions}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={12}>
                  <div className="mb-1">
                    <Label className="form-label mb-0">Extended Details</Label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      value=""
                      readOnly
                    />
                  </div>
                </Col>
              </Row>
            </Form>
          </TabPane>

          {/* New Tab */}
          <TabPane tabId="4">
            <Form>
              {/* Main Account Information */}
              <Row className="mb-2">
                <Col md={2}>
                  <FormInput
                    id="newAccountId"
                    label="Account Id"
                    value={accountForm.accountId}
                    onChange={(val) => handleAccountFormChange('accountId', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newName"
                    label="Name *"
                    value={accountForm.name}
                    onChange={(val) => handleAccountFormChange('name', val)}
                    required
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newLocation"
                    label="Location"
                    value={accountForm.location}
                    onChange={(val) => handleAccountFormChange('location', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newTradingName"
                    label="Trading Name"
                    value={accountForm.tradingName}
                    onChange={(val) => handleAccountFormChange('tradingName', val)}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Type"
                    id="type"
                    options={typeOptions}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Industry *"
                    id="industry"
                    options={industryOptions}
                  />
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={2}>
                  <FormInput
                    id="newACN"
                    label="ACN"
                    value={accountForm.acn}
                    onChange={(val) => handleAccountFormChange('acn', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newABN"
                    label="ABN"
                    value={accountForm.abn}
                    onChange={(val) => handleAccountFormChange('abn', val)}
                  />
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={2}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Status"
                    id="statusValue"
                    options={statusOptions}
                  />
                </Col>
                <Col md={2}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Rating"
                    id="rating"
                    options={ratingOptions}
                  />
                </Col>
                <Col md={2}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newActive"
                      checked={accountForm.active}
                      onChange={() => handleAccountFormCheckbox('active')}
                    />
                    <Label className="form-check-label" htmlFor="newActive">
                      Active
                    </Label>
                  </div>
                </Col>
                <Col md={2}>
                  <div className="form-check mt-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="newSupplier"
                      checked={accountForm.supplier}
                      onChange={() => handleAccountFormCheckbox('supplier')}
                    />
                    <Label className="form-check-label" htmlFor="newSupplier">
                      Supplier
                    </Label>
                  </div>
                </Col>
              </Row>

              {/* Contact Information */}
              <Row className="mb-2">
                <Col md={3}>
                  <FormInput
                    id="newMainPhoneNumber"
                    label="Main Phone Number"
                    value={accountForm.mainPhoneNumber}
                    onChange={(val) => handleAccountFormChange('mainPhoneNumber', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newMainFaxNumber"
                    label="Main Fax Number"
                    value={accountForm.mainFaxNumber}
                    onChange={(val) => handleAccountFormChange('mainFaxNumber', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newMainEmailAddress"
                    label="Main Email Address"
                    value={accountForm.mainEmailAddress}
                    onChange={(val) => handleAccountFormChange('mainEmailAddress', val)}
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newWebAddress"
                    label="Web Address"
                    value={accountForm.webAddress}
                    onChange={(val) => handleAccountFormChange('webAddress', val)}
                  />
                </Col>
              </Row>

              {/* Account Relationships */}
              <Row className="mb-2">
                <Col md={3}>
                  <FormInput
                    id="newAccountOwner"
                    label="Account Owner"
                    value={accountForm.accountOwner}
                    onChange={(val) => handleAccountFormChange('accountOwner', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newPrimaryContact"
                    label="Primary Contact"
                    value={accountForm.primaryContact}
                    onChange={(val) => handleAccountFormChange('primaryContact', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newParentAccount"
                    label="Parent Account"
                    value={accountForm.parentAccount}
                    onChange={(val) => handleAccountFormChange('parentAccount', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <SelectInput
                    formData={accountForm}
                    setFormData={setAccountForm}
                    label="Payment Terms"
                    id="paymentTerms"
                    options={paymentTermsOptions}
                  />
                </Col>
              </Row>

              {/* Address Information */}
              <Row className="mb-3">
                <Col md={6}>
                  <fieldset className="border p-2">
                    <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.8rem' }}>Billing Address</legend>
                    <Row>
                      <Col md={6}>
                        <FormInput
                          id="billingAddr1"
                          label="Addr 1"
                          value={accountForm.billingAddr1}
                          onChange={(val) => handleAccountFormChange('billingAddr1', val)}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          id="billingAddr2"
                          label="Addr 2"
                          value={accountForm.billingAddr2}
                          onChange={(val) => handleAccountFormChange('billingAddr2', val)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormInput
                          id="billingAddr3"
                          label="Addr 3"
                          value={accountForm.billingAddr3}
                          onChange={(val) => handleAccountFormChange('billingAddr3', val)}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          id="billingPostCode"
                          label="Post Code"
                          value={accountForm.billingPostCode}
                          onChange={(val) => handleAccountFormChange('billingPostCode', val)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <SelectInput
                          formData={accountForm}
                          setFormData={setAccountForm}
                          label="Country"
                          id="billingCountry"
                          options={countryOptions}
                        />
                      </Col>
                      <Col md={6}>
                        <SelectInput
                          formData={accountForm}
                          setFormData={setAccountForm}
                          label="State/Province"
                          id="billingStateProvince"
                          options={stateProvinceOptions}
                        />
                      </Col>
                    </Row>
                  </fieldset>
                </Col>
                <Col md={6}>
                  <fieldset className="border p-2">
                    <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.8rem' }}>Shipping Address</legend>
                    <div className="form-check mb-2">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="copyFromBilling"
                        checked={accountForm.copyFromBilling}
                        onChange={() => {
                          handleAccountFormCheckbox('copyFromBilling');
                          handleCopyFromBilling();
                        }}
                      />
                      <Label className="form-check-label" htmlFor="copyFromBilling">
                        Copy from Billing
                      </Label>
                    </div>
                    <Row>
                      <Col md={6}>
                        <FormInput
                          id="shippingAddr1"
                          label="Addr 1"
                          value={accountForm.shippingAddr1}
                          onChange={(val) => handleAccountFormChange('shippingAddr1', val)}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          id="shippingAddr2"
                          label="Addr 2"
                          value={accountForm.shippingAddr2}
                          onChange={(val) => handleAccountFormChange('shippingAddr2', val)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormInput
                          id="shippingAddr3"
                          label="Addr 3"
                          value={accountForm.shippingAddr3}
                          onChange={(val) => handleAccountFormChange('shippingAddr3', val)}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          id="shippingPostCode"
                          label="Post Code"
                          value={accountForm.shippingPostCode}
                          onChange={(val) => handleAccountFormChange('shippingPostCode', val)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <SelectInput
                          formData={accountForm}
                          setFormData={setAccountForm}
                          label="Country"
                          id="shippingCountry"
                          options={countryOptions}
                        />
                      </Col>
                      <Col md={6}>
                        <SelectInput
                          formData={accountForm}
                          setFormData={setAccountForm}
                          label="State/Province"
                          id="shippingStateProvince"
                          options={stateProvinceOptions}
                        />
                      </Col>
                    </Row>
                  </fieldset>
                </Col>
              </Row>

              {/* Order Defaults and Comments */}
              <Row className="mb-2">
                <Col md={6}>
                  <fieldset className="border p-2">
                    <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.8rem' }}>Order Defaults</legend>
                    <Row>
                      <Col md={6}>
                        <SelectInput
                          formData={accountForm}
                          setFormData={setAccountForm}
                          label="Warehouse"
                          id="warehouse"
                          options={warehouseOptions}
                        />
                      </Col>
                      <Col md={6}>
                        <SelectInput
                          formData={accountForm}
                          setFormData={setAccountForm}
                          label="Price List"
                          id="priceList"
                          options={priceListOptions}
                        />
                      </Col>
                    </Row>
                  </fieldset>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label className="form-label mb-0">Delivery Comments</Label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={4}
                      value={accountForm.deliveryComments}
                      onChange={(e) => handleAccountFormChange('deliveryComments', e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <div></div>
        <div className="d-flex gap-2 justify-content-end">
          {activeTab === '4' ? (
            // New tab buttons
            <>
              <button className="btn btn-soft-warning btn-sm" style={{ minWidth: 80 }} onClick={handleNewAccountClear}>Clear</button>
              <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }}>Check</button>
              <button className="btn btn-soft-success btn-sm" style={{ minWidth: 80 }}>Create</button>
            </>
          ) : activeTab === '3' ? (
            // View tab buttons
            <>
              <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }}>Prev</button>
              <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }}>Next</button>
              <button className="btn btn-soft-success btn-sm" style={{ minWidth: 80 }} onClick={handleSelect}>Pick</button>
            </>
          ) : activeTab === '1' ? (
            // Search tab buttons
            <>
              <button className="btn btn-soft-warning btn-sm" style={{ minWidth: 80 }} onClick={handleClear}>Clear</button>
            </>
          ) : null}
          <button className="btn btn-soft-dark btn-sm" style={{ minWidth: 80 }} onClick={handleCancel}>Cancel</button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default AccountOwnerModal; 